import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { JobShare, ShareRecipientOrg, ShareRecipientUser } from '../../types';

interface ShareJobDialogProps {
  open: boolean;
  jobId: number;
  jobName?: string;
  onClose: () => void;
}

const ShareJobDialog: React.FC<ShareJobDialogProps> = ({ open, jobId, jobName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [users, setUsers] = useState<ShareRecipientUser[]>([]);
  const [organizations, setOrganizations] = useState<ShareRecipientOrg[]>([]);
  const [shares, setShares] = useState<JobShare[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<ShareRecipientUser[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [recipients, existing] = await Promise.all([
        apiService.getShareRecipients(),
        apiService.getJobShares(jobId),
      ]);
      setUsers(recipients.users);
      setOrganizations(recipients.organizations);
      setShares(existing);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load sharing options.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedUsers([]);
      setSelectedOrgIds([]);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  // Anyone already shared with is filtered out of the picker rather than shown as
  // a duplicate option - re-selecting them would be a no-op on the backend anyway.
  const sharedUpns = useMemo(
    () => new Set(shares.filter((s) => s.shared_with_upn).map((s) => s.shared_with_upn as string)),
    [shares]
  );
  const sharedOrgIds = useMemo(
    () => new Set(shares.filter((s) => s.shared_with_org_id).map((s) => s.shared_with_org_id as number)),
    [shares]
  );

  const availableUsers = useMemo(
    () => users.filter((u) => !sharedUpns.has(u.upn)),
    [users, sharedUpns]
  );

  const handleShare = async () => {
    if (selectedUsers.length === 0 && selectedOrgIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiService.shareJob(
        jobId,
        selectedUsers.map((u) => u.upn),
        selectedOrgIds
      );
      setShares(updated);
      setSelectedUsers([]);
      setSelectedOrgIds([]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not share this report.');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (share: JobShare) => {
    setError(null);
    try {
      await apiService.revokeJobShare(
        jobId,
        share.shared_with_upn
          ? { upn: share.shared_with_upn }
          : { org_id: share.shared_with_org_id as number }
      );
      setShares((prev) => prev.filter((s) => s.id !== share.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not remove access.');
    }
  };

  const toggleOrg = (orgId: number) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const nothingSelected = selectedUsers.length === 0 && selectedOrgIds.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share Report
        {jobName && (
          <Typography variant="body2" color="text.secondary">
            {jobName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              multiple
              options={availableUsers}
              value={selectedUsers}
              onChange={(_, value) => setSelectedUsers(value)}
              // displayName, never the UPN - guest accounts render as
              // name_domain.com#EXT#@tenant.onmicrosoft.com otherwise
              getOptionLabel={(option) => option.display_name}
              isOptionEqualToValue={(option, value) => option.upn === value.upn}
              renderOption={(props, option) => (
                <li {...props} key={option.upn}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 30, height: 30 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={option.display_name} secondary={option.email || undefined} />
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Share with people" placeholder="Search by name…" />
              )}
              noOptionsText={
                users.length === 0
                  ? 'No one available to share with'
                  : 'Everyone available already has access'
              }
            />

            {organizations.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Or share with an entire organization
                </Typography>
                {organizations.map((org) => {
                  const already = sharedOrgIds.has(org.id);
                  return (
                    <Box key={org.id} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={selectedOrgIds.includes(org.id) || already}
                        disabled={already}
                        onChange={() => toggleOrg(org.id)}
                      />
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Everyone at {org.name}
                        {already && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}
                            — already shared
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {users.length === 0 && organizations.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You are not a member of any sharing group, so there is no one to share with yet.
              </Alert>
            )}

            {shares.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Currently shared with
                </Typography>
                <List dense>
                  {shares.map((share) => (
                    <ListItem
                      key={share.id}
                      secondaryAction={
                        <Tooltip title="Remove access">
                          <IconButton edge="end" size="small" onClick={() => handleRevoke(share)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: 30, height: 30 }}>
                          {share.shared_with_org_id ? (
                            <BusinessIcon fontSize="small" />
                          ) : (
                            <PersonIcon fontSize="small" />
                          )}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={share.shared_with_name || share.shared_with_upn}
                        secondary={share.shared_with_org_id ? 'Entire organization' : undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              People you share with can view this report, but cannot edit or delete it.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={nothingSelected || saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Sharing…' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareJobDialog;
